/**
 * Bảng dữ liệu dùng chung — chuẩn thành phần mục 3.1 đặc tả giao diện.
 *
 * Hiện thực MỘT LẦN ở đây cho toàn hệ thống; các đợt sau dùng lại, không sao chép.
 *
 * Bảng KHÔNG tự truy vấn: mọi việc lọc, đếm và phân trang diễn ra ở phía máy chủ
 * (chương 12) và nơi gọi truyền kết quả vào. Bảng cũng KHÔNG có chế độ tải tất cả.
 *
 * Đủ năm trạng thái Bảng 3.3: khung xương, có dữ liệu, rỗng vì chưa có dữ liệu,
 * rỗng vì bộ lọc, lỗi. Hai trạng thái rỗng dùng hai thành phần khác nhau.
 */
import type { ReactNode } from 'react';
import { dienVao, vi } from '../ngon-ngu/vi';
import { KhungXuong, TrangThaiLoi, TrangThaiRong, TrangThaiRongLoc } from './TrangThaiManHinh';
import type { TenTrangThai } from '../trang-thai/dung-tai-du-lieu';

/** Số dòng mỗi trang: mặc định 20, tối đa 200; không có lựa chọn "tất cả". */
export const SO_DONG_CHON = [20, 50, 100, 200] as const;
export const SO_DONG_MAC_DINH = 20;

export interface CotBang<T> {
  khoa: string;
  nhan: string;
  /** Chỉ cột có chỉ mục tương ứng trong cơ sở dữ liệu mới bật sắp xếp. */
  sapXepDuoc?: boolean;
  ve: (dong: T) => ReactNode;
  canPhai?: boolean;
  /** Căn giữa cả tiêu đề và dữ liệu của cột. */
  canGiua?: boolean;
  /** Phần thêm cạnh nhãn tiêu đề cột (vd nút kính lúp mở ô tìm). */
  phuTieuDe?: ReactNode;
}

export interface BangDuLieuProps<T> {
  /** Nhãn cho trình đọc màn hình. */
  nhan: string;
  cot: CotBang<T>[];
  dong: T[];
  khoaDong: (d: T) => string;
  /** Nhãn dễ đọc của một dòng, dùng cho hộp kiểm chọn dòng (trình đọc màn hình). */
  nhanDong?: (d: T) => string;
  trangThai: TenTrangThai;
  loi: { ma: string; thongBao: string; requestId?: string } | null;
  taiLau: boolean;
  onThuLai: () => void;

  /* Phân trang phía máy chủ */
  tong: number;
  trang: number;
  soDongMoiTrang: number;
  onDoiTrang: (t: number) => void;
  onDoiSoDong: (s: number) => void;

  /* Sắp xếp phía máy chủ */
  sapXep?: string;
  huongSapXep?: 'asc' | 'desc';
  onDoiSapXep?: (khoa: string, huong: 'asc' | 'desc') => void;

  /* Chọn dòng cho thao tác hàng loạt */
  daChon?: Set<string>;
  onDoiChon?: (ds: Set<string>) => void;
  /** Đã bật "chọn toàn bộ kết quả lọc" chưa. */
  chonToanBoKetQua?: boolean;
  onChonToanBoKetQua?: (bat: boolean) => void;
  /** Thanh hành động hàng loạt; chỉ hiện khi có ít nhất một dòng được chọn. */
  hanhDongHangLoat?: (daChon: string[]) => ReactNode;
  /** Ẩn hẳn thanh "Đã chọn N dòng" (màn hình đã có nút thao tác riêng trên thanh công cụ). */
  anThanhHangLoat?: boolean;

  /* Bộ lọc và trạng thái rỗng */
  dangLoc: boolean;
  onXoaLoc: () => void;
  moTaRong?: string;
  nhanHanhDongRong?: string;
  onHanhDongRong?: () => void;

  /* Dòng */
  /** Lớp CSS thêm cho một dòng (vd dòng thông báo chưa đọc in đậm). */
  lopDong?: (d: T) => string | undefined;
  /** Bấm vào dòng (ngoài các nút/liên kết trong dòng). */
  onBamDong?: (d: T) => void;

  /* Tự làm mới */
  dangTamDungLamMoi?: boolean;
  lyDoTamDung?: 'tab-an' | 'dang-chon' | null;
  onLamMoiNgay?: () => void;
}

/**
 * Các số trang hiển thị: đủ cả khi ≤ 7 trang; nhiều hơn thì trang đầu, cuối, hai
 * trang quanh trang hiện tại và dấu ngắt (`null`) — như phân trang của mẫu.
 */
export function soTrangHienThi(trang: number, soTrang: number): (number | null)[] {
  if (soTrang <= 7) return Array.from({ length: soTrang }, (_, i) => i + 1);
  let dau = Math.max(2, trang - 2);
  let cuoi = Math.min(soTrang - 1, trang + 2);
  if (trang <= 4) {
    dau = 2;
    cuoi = 5;
  }
  if (trang >= soTrang - 3) {
    dau = soTrang - 4;
    cuoi = soTrang - 1;
  }
  const kq: (number | null)[] = [1];
  if (dau > 2) kq.push(null);
  for (let i = dau; i <= cuoi; i++) kq.push(i);
  if (cuoi < soTrang - 1) kq.push(null);
  kq.push(soTrang);
  return kq;
}

export function BangDuLieu<T>(p: BangDuLieuProps<T>): ReactNode {
  const daChon = p.daChon ?? new Set<string>();
  const coChon = p.onDoiChon !== undefined;
  const khoaTrang = p.dong.map(p.khoaDong);
  const caTrangDaChon = khoaTrang.length > 0 && khoaTrang.every((k) => daChon.has(k));

  const doiMotDong = (k: string): void => {
    if (!p.onDoiChon) return;
    const moi = new Set(daChon);
    if (moi.has(k)) moi.delete(k);
    else moi.add(k);
    p.onDoiChon(moi);
  };

  const doiCaTrang = (): void => {
    if (!p.onDoiChon) return;
    const moi = new Set(daChon);
    // Hộp kiểm ở đầu bảng CHỈ chọn các dòng của trang hiện tại (Bảng 3.1).
    if (caTrangDaChon) for (const k of khoaTrang) moi.delete(k);
    else for (const k of khoaTrang) moi.add(k);
    p.onDoiChon(moi);
  };

  const soTrang = Math.max(1, Math.ceil(p.tong / p.soDongMoiTrang));
  const tu = p.tong === 0 ? 0 : (p.trang - 1) * p.soDongMoiTrang + 1;
  const den = Math.min(p.tong, p.trang * p.soDongMoiTrang);

  // Hàng tiêu đề cột dùng chung cho bảng có dữ liệu và bảng rỗng: theo mẫu, bảng
  // rỗng vẫn giữ tiêu đề cột và hiện thông báo trong thân bảng.
  const dauBang = (
    <thead>
      <tr>
        {coChon ? (
          <th scope="col" className="bang__o-chon">
            <input
              type="checkbox"
              checked={caTrangDaChon}
              onChange={doiCaTrang}
              aria-label={vi.bang.chonTatCaTrang}
            />
          </th>
        ) : null}
        {p.cot.map((c) => {
          const dangSap = p.sapXep === c.khoa;
          const huong = dangSap ? (p.huongSapXep ?? 'asc') : undefined;
          return (
            <th
              key={c.khoa}
              scope="col"
              aria-sort={dangSap ? (huong === 'desc' ? 'descending' : 'ascending') : undefined}
              className={c.canGiua ? 'bang__giua' : c.canPhai ? 'bang__phai' : undefined}
            >
              {/* Cột không sắp xếp được thì KHÔNG có nút, tránh để người
                  dùng bấm vào chỗ không có tác dụng (Bảng 3.1). */}
              {c.sapXepDuoc && p.onDoiSapXep ? (
                <button
                  type="button"
                  className="bang__sap-xep"
                  onClick={() => p.onDoiSapXep?.(c.khoa, dangSap && huong === 'asc' ? 'desc' : 'asc')}
                  aria-label={`${c.nhan}, ${
                    dangSap && huong === 'asc' ? vi.bang.sapXepGiam : vi.bang.sapXepTang
                  }`}
                >
                  {c.nhan}
                  <span aria-hidden="true">{dangSap ? (huong === 'desc' ? ' ↓' : ' ↑') : ' ↕'}</span>
                </button>
              ) : (
                c.nhan
              )}
              {c.phuTieuDe}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  return (
    <div className="bang-du-lieu" data-vung="bang-du-lieu">
      {/* Thanh hành động hàng loạt chỉ hiện khi có ít nhất một dòng được chọn. */}
      {coChon && daChon.size > 0 && !p.anThanhHangLoat ? (
        <div className="bang-du-lieu__hang-loat" data-vung="thanh-hang-loat">
          <span>
            {vi.bang.daChon} {daChon.size} {vi.bang.dong}
          </span>
          {p.hanhDongHangLoat?.([...daChon])}
          <button type="button" className="nut nut--nho" onClick={() => p.onDoiChon?.(new Set())}>
            {vi.bang.boChon}
          </button>
        </div>
      ) : null}

      {/* Dòng gợi ý chọn toàn bộ kết quả lọc, tách riêng khỏi hộp kiểm đầu bảng. */}
      {coChon && caTrangDaChon && p.tong > p.dong.length && p.onChonToanBoKetQua ? (
        <div className="bang-du-lieu__chon-tat-ca" data-vung="chon-toan-bo">
          <button
            type="button"
            className="nut nut--lien-ket"
            onClick={() => p.onChonToanBoKetQua?.(!p.chonToanBoKetQua)}
            aria-pressed={p.chonToanBoKetQua ?? false}
          >
            {dienVao(vi.bang.chonTatCaKetQua, { so: p.tong.toLocaleString('vi-VN') })}
          </button>
          <span className="bang-du-lieu__ghi-chu">{vi.bang.chonTatCaKetQuaGhiChu}</span>
        </div>
      ) : null}

      {p.onLamMoiNgay ? (
        <div className="bang-du-lieu__lam-moi" data-vung="tu-lam-moi">
          <span data-tu-lam-moi={p.dangTamDungLamMoi ? 'tam-dung' : 'dang-chay'}>
            {p.dangTamDungLamMoi
              ? p.lyDoTamDung === 'tab-an'
                ? vi.bang.tamDungLamMoiTabAn
                : vi.bang.tamDungLamMoi
              : vi.bang.tuLamMoi}
          </span>
          <button type="button" className="nut nut--nho" onClick={p.onLamMoiNgay}>
            {vi.bang.lamMoiNgay}
          </button>
        </div>
      ) : null}

      {p.trangThai === 'dang-tai' ? (
        <KhungXuong soDong={Math.min(p.soDongMoiTrang, 10)} soCot={p.cot.length} taiLau={p.taiLau} />
      ) : null}

      {p.trangThai === 'loi' && p.loi ? <TrangThaiLoi loi={p.loi} onThuLai={p.onThuLai} /> : null}

      {p.trangThai === 'rong' || p.trangThai === 'rong-loc' ? (
        <>
          {/* Theo mẫu: bảng rỗng vẫn giữ hàng tiêu đề cột; thông báo rỗng đặt dưới,
              NGOÀI khung cuộn ngang để luôn nằm giữa card. */}
          <div className="bang-cuon">
            <table className="bang bang--rong" aria-label={p.nhan}>
              {dauBang}
            </table>
          </div>
          <div className="bang__rong">
            {p.trangThai === 'rong' ? (
              <TrangThaiRong
                moTa={p.moTaRong}
                nhanHanhDong={p.nhanHanhDongRong}
                onHanhDong={p.onHanhDongRong}
              />
            ) : (
              <TrangThaiRongLoc onXoaLoc={p.onXoaLoc} />
            )}
          </div>
        </>
      ) : null}

      {p.trangThai === 'co-du-lieu' ? (
        <>
          {/* Bảng rộng hơn card thì cuộn ngang trong khung, không tràn ra ngoài. */}
          <div className="bang-cuon">
            <table className="bang" aria-label={p.nhan}>
              {dauBang}
              <tbody>
                {p.dong.map((d) => {
                  const k = p.khoaDong(d);
                  return (
                    <tr
                      key={k}
                      data-khoa-dong={k}
                      aria-selected={daChon.has(k) || undefined}
                      className={
                        [p.lopDong?.(d), p.onBamDong ? 'bang__dong--bam-duoc' : undefined]
                          .filter(Boolean)
                          .join(' ') || undefined
                      }
                      onClick={
                        p.onBamDong
                          ? (e) => {
                              // Bấm vào nút/liên kết/ô nhập trong dòng thì để thành phần đó tự xử lý.
                              if ((e.target as HTMLElement).closest('button, a, input, select, label'))
                                return;
                              p.onBamDong?.(d);
                            }
                          : undefined
                      }
                    >
                      {coChon ? (
                        <td className="bang__o-chon">
                          <input
                            type="checkbox"
                            checked={daChon.has(k)}
                            onChange={() => doiMotDong(k)}
                            aria-label={`${vi.bang.chonMotDong}: ${p.nhanDong?.(d) ?? k}`}
                          />
                        </td>
                      ) : null}
                      {p.cot.map((c) => (
                        <td
                          key={c.khoa}
                          className={c.canGiua ? 'bang__giua' : c.canPhai ? 'bang__phai' : undefined}
                        >
                          {c.ve(d)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mẫu Pagination: "Tổng N bản ghi" bên trái; nút trang ‹ 1 2 … › và số
              dòng mỗi trang bên phải. */}
          <nav className="bang__phan-trang" aria-label={vi.bang.trang}>
            <span data-vung="phan-trang-tong" title={`${vi.bang.hienThi} ${tu}-${den}`}>
              {dienVao(vi.bang.tongBanGhi, { so: p.tong.toLocaleString('vi-VN') })}
            </span>
            <span className="phan-trang__phai">
              <button
                type="button"
                className="phan-trang__nut"
                disabled={p.trang <= 1}
                onClick={() => p.onDoiTrang(p.trang - 1)}
                aria-label={vi.bang.truoc}
              >
                ‹
              </button>
              {soTrangHienThi(p.trang, soTrang).map((x, i) =>
                x === null ? (
                  <span key={`gian-${i}`} className="phan-trang__gian" aria-hidden="true">
                    •••
                  </span>
                ) : (
                  <button
                    key={x}
                    type="button"
                    className="phan-trang__nut"
                    aria-current={x === p.trang ? 'page' : undefined}
                    aria-label={`${vi.bang.trang} ${x}`}
                    onClick={() => p.onDoiTrang(x)}
                  >
                    {x}
                  </button>
                ),
              )}
              <button
                type="button"
                className="phan-trang__nut"
                disabled={p.trang >= soTrang}
                onClick={() => p.onDoiTrang(p.trang + 1)}
                aria-label={vi.bang.sau}
              >
                ›
              </button>
              <label className="chi-cho-doc-man-hinh" htmlFor="so-dong-moi-trang">
                {vi.bang.soDongMoiTrang}
              </label>
              <select
                id="so-dong-moi-trang"
                className="phan-trang__so-dong"
                value={p.soDongMoiTrang}
                onChange={(e) => p.onDoiSoDong(Number(e.target.value))}
              >
                {SO_DONG_CHON.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <span aria-hidden="true">{vi.bang.moiTrang}</span>
            </span>
          </nav>
        </>
      ) : null}
    </div>
  );
}
